import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Flex,
  Text,
  VStack,
  HStack,
  Icon,
  Badge,
  Spinner,
  Button,
  Avatar,
  Heading,
  Card,
  CardBody,
  Collapse,
  Tooltip,
  useColorModeValue,
} from '@chakra-ui/react';
import {
  FaTrophy,
  FaMedal,
  FaAward,
  FaChevronRight,
  FaChevronDown,
  FaChevronUp,
} from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { getLocalyticsRankings, getRankingTier, getRankingColor, formatRanking } from '../services/localyticsRankingService';
import { getBusinessImages } from '../services/imageService';
import { useUser } from '../contexts/UserContext';
import { logger } from '../utils/logger';

const MotionBox = motion(Box);

/**
 * Business Ranking List Item Component
 */
const BusinessRankingListItem = ({ business, position, totalBusinesses, borderColor, textColor, subtitleColor, cardBg, hoverBg }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const tier = getRankingTier(business.localytics_score, totalBusinesses, position);
  const rankingColor = getRankingColor(business.localytics_score, totalBusinesses);

  // Highlight top 3 with special border colors
  const itemBorderColor = position === 1 ? 'yellow.300' : position === 2 ? 'gray.300' : position === 3 ? 'orange.300' : borderColor;

  return (
    <Card
      bg={cardBg}
      borderWidth="1px"
      borderColor={itemBorderColor}
      borderRadius="xl"
      overflow="hidden"
      transition="all 0.3s"
      _hover={{ shadow: 'md', borderColor: 'blue.300' }}
      mb={3}
    >
      <CardBody p={0}>
        <Flex
          p={4}
          cursor="pointer"
          onClick={() => setIsExpanded(!isExpanded)}
          _hover={{ bg: hoverBg }}
          transition="background 0.2s"
          align="center"
          justify="space-between"
        >
          <HStack spacing={3} flex={1}>
            {/* Position Badge */}
            <Flex
              w="32px"
              h="32px"
              borderRadius="full"
              bg={position <= 3 ? (position === 1 ? 'yellow.400' : position === 2 ? 'gray.300' : 'orange.300') : 'blue.100'}
              justify="center"
              align="center"
              fontWeight="bold"
              color={position <= 3 ? 'white' : 'blue.600'}
              fontSize="sm"
              flexShrink={0}
            >
              {position}
            </Flex>

            {/* Business Name and Tier */}
            <VStack align="start" spacing={0} flex={1}>
              <Text fontWeight="bold" fontSize="md" color={textColor} noOfLines={1}>
                {business.business_name}
              </Text>
              <HStack spacing={2}>
                <Badge colorScheme={tier.colorScheme} fontSize="2xs" px={2} py={0.5} borderRadius="full">
                  {tier.emoji} {tier.label.toUpperCase()}
                </Badge>
              </HStack>
            </VStack>
          </HStack>

          {/* Ranking Display */}
          <HStack spacing={4}>
            <VStack spacing={0} align="center">
              <Box
                w="50px"
                h="50px"
                borderRadius="full"
                bg={`${rankingColor}20`}
                border="3px solid"
                borderColor={rankingColor}
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <Text fontWeight="bold" fontSize="lg" color={rankingColor}>
                  {formatRanking(business.localytics_score)}
                </Text>
              </Box>
              <Text fontSize="2xs" color={subtitleColor}>
                Score
              </Text>
            </VStack>

            <Icon
              as={isExpanded ? FaChevronUp : FaChevronDown}
              color={subtitleColor}
              boxSize={4}
            />
          </HStack>
        </Flex>

        {/* Expanded Details */}
        <Collapse in={isExpanded} animateOpacity>
          <Box borderTopWidth="1px" borderColor={borderColor} p={4} bg={hoverBg}>
            <HStack spacing={6} wrap="wrap">
              <VStack align="start" spacing={1}>
                <Text fontSize="xs" color={subtitleColor} fontWeight="medium">Growth Rank</Text>
                <Text fontSize="sm" fontWeight="bold" color={textColor}>
                  #{business.rankings?.growth || 'N/A'}
                </Text>
              </VStack>
              <VStack align="start" spacing={1}>
                <Text fontSize="xs" color={subtitleColor} fontWeight="medium">Reviews Rank</Text>
                <Text fontSize="sm" fontWeight="bold" color={textColor}>
                  #{business.rankings?.total_reviews || 'N/A'}
                </Text>
              </VStack>
              <VStack align="start" spacing={1}>
                <Text fontSize="xs" color={subtitleColor} fontWeight="medium">Sentiment Rank</Text>
                <Text fontSize="sm" fontWeight="bold" color={textColor}>
                  #{business.rankings?.sentiment || 'N/A'}
                </Text>
              </VStack>
              <VStack align="start" spacing={1}>
                <Text fontSize="xs" color={subtitleColor} fontWeight="medium">Traffic Rank</Text>
                <Text fontSize="sm" fontWeight="bold" color={textColor}>
                  #{business.rankings?.traffic || 'N/A'}
                </Text>
              </VStack>
            </HStack>
          </Box>
        </Collapse>
      </CardBody>
    </Card>
  );
};

/**
 * LocalyticsScoreWidget - Displays top businesses by Localytics Score
 * Includes podium view for top 3 and list view for all businesses
 * 
 * Note: Higher score is better (100 is perfect, 0-100 scale)
 */
const LocalyticsScoreWidget = () => {
  const navigate = useNavigate();
  const { brandName } = useUser();
  const [rankingData, setRankingData] = useState(null);
  const [businessImages, setBusinessImages] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [showAllBusinesses, setShowAllBusinesses] = useState(false);

  // Color mode values
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const textColor = useColorModeValue('gray.700', 'gray.200');
  const subtitleColor = useColorModeValue('gray.500', 'gray.400');
  const hoverBg = useColorModeValue('gray.50', 'gray.700');

  useEffect(() => {
    let mounted = true;

    const fetchData = async () => {
      try {
        // Fetch rankings
        const rankings = await getLocalyticsRankings();
        
        if (mounted) {
          setRankingData(rankings);
        }

        // Fetch business images
        const images = await getBusinessImages().catch(err => {
          logger.log('Images not available:', err.message);
          return [];
        });

        if (mounted && images) {
          // Create name -> image URL mapping
          const nameToImage = {};
          images.forEach(img => {
            if (img.business_name && img.high_res_image_url_fetched) {
              nameToImage[img.business_name] = img.high_res_image_url_fetched;
            }
          });
          setBusinessImages(nameToImage);
        }
      } catch (error) {
        logger.error('Error fetching Localytics Score data:', error);
      } finally {
        if (mounted) {
          setIsLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      mounted = false;
    };
  }, []);

  // Get sorted businesses by score (higher is better, so already sorted)
  const sortedBusinesses = useMemo(() => {
    if (!rankingData?.rankingResults) return [];
    return rankingData.rankingResults.filter(b => 
      b.localytics_score !== null && b.localytics_score !== undefined
    );
  }, [rankingData]);

  // Get top 3 for podium
  const top3Businesses = useMemo(() => {
    return sortedBusinesses.slice(0, 3);
  }, [sortedBusinesses]);

  const handleViewDetails = () => {
    navigate('/dashboards/localytics-score');
  };

  // Loading state
  if (isLoading) {
    return (
      <Box
        bg={cardBg}
        borderWidth="1px"
        borderColor={borderColor}
        borderRadius="2xl"
        p={6}
        boxShadow="sm"
      >
        <Flex align="center" justify="center" minH="300px">
          <VStack spacing={3}>
            <Spinner size="lg" color="purple.500" thickness="3px" />
            <Text color={subtitleColor} fontSize="sm">Loading {brandName} Scores...</Text>
          </VStack>
        </Flex>
      </Box>
    );
  }

  // No data state
  if (!rankingData || sortedBusinesses.length === 0) {
    return (
      <Box
        bg={cardBg}
        borderWidth="1px"
        borderColor={borderColor}
        borderRadius="2xl"
        p={6}
        boxShadow="sm"
      >
        <VStack spacing={3} py={8}>
          <Icon as={FaTrophy} boxSize={10} color="gray.300" />
          <Text color={subtitleColor}>No {brandName} Score data available</Text>
        </VStack>
      </Box>
    );
  }

  const totalBusinesses = rankingData.totalBusinesses;

  return (
    <MotionBox
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      bg={cardBg}
      borderRadius="2xl"
      borderWidth="1px"
      borderColor={borderColor}
      p={6}
      boxShadow="sm"
    >
      {/* Header */}
      <Flex justify="space-between" align="center" mb={6}>
        <HStack spacing={3}>
          <Icon as={FaTrophy} color="yellow.400" boxSize={6} />
          <Heading size="md" textTransform="uppercase" fontStyle="italic" fontSize="1.3em">
            {brandName} Ranking
          </Heading>
        </HStack>
        <Button
          size="sm"
          colorScheme="purple"
          variant="ghost"
          rightIcon={<FaChevronRight />}
          onClick={handleViewDetails}
        >
          View Full Rankings
        </Button>
      </Flex>

      {/* Top 3 Podium */}
      {top3Businesses.length >= 3 && (
        <Box mb={6}>
          <Flex justify="center" align="flex-end" gap={4} w="full">
            {/* Silver - Second Place */}
            <Box
              bg={cardBg}
              borderRadius="xl"
              textAlign="center"
              position="relative"
              transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
              _hover={{ 
                transform: 'translateY(-4px)', 
                shadow: 'xl',
              }}
              overflow="hidden"
              flex="1"
              maxW="200px"
              boxShadow="lg"
              borderWidth="1px"
              borderColor={borderColor}
            >
              <Box h="3px" bgGradient="linear(to-r, gray.300, gray.500, gray.300)" />
              <VStack p={4} spacing={3}>
                <Flex
                  position="absolute"
                  top={3}
                  left={3}
                  minW="28px"
                  h="28px"
                  px={2}
                  borderRadius="full"
                  bgGradient="linear(to-br, gray.300, gray.500)"
                  justify="center"
                  align="center"
                  shadow="md"
                >
                  <Text color="white" fontWeight="bold" fontSize="md" lineHeight="1">2</Text>
                </Flex>
                
                <Icon as={FaMedal} color="gray.400" boxSize={5} />
                
                <Box p="2px" borderRadius="full" bgGradient="linear(to-br, gray.300, gray.500)">
                  <Avatar 
                    name={top3Businesses[1]?.business_name} 
                    src={businessImages[top3Businesses[1]?.business_name] || ''} 
                    bg="gray.100" 
                    boxSize="50px"
                    borderWidth="2px"
                    borderColor={cardBg}
                  />
                </Box>
                
                <VStack spacing={0}>
                  <Tooltip label={top3Businesses[1]?.business_name} placement="top" hasArrow openDelay={300}>
                    <Text fontSize="sm" fontWeight="bold" noOfLines={1} color={textColor}>
                      {top3Businesses[1]?.business_name}
                    </Text>
                  </Tooltip>
                  <Text 
                    fontWeight="extrabold" 
                    fontSize="xl" 
                    bgGradient="linear(to-r, gray.600, gray.800)"
                    bgClip="text"
                  >
                    {formatRanking(top3Businesses[1]?.localytics_ranking)}
                  </Text>
                  <Text fontSize="2xs" color={subtitleColor} fontWeight="medium" textTransform="uppercase" letterSpacing="wider">
                    {brandName} Ranking
                  </Text>
                </VStack>
                
                <Box w="full" px={2}>
                  <Box h="4px" bg="gray.100" borderRadius="full" overflow="hidden">
                    <Box
                      h="full"
                      w={`${Math.max(20, top3Businesses[1]?.localytics_score || 0)}%`}
                      bgGradient="linear(to-r, gray.400, gray.500)"
                      borderRadius="full"
                      transition="width 0.5s ease-out"
                    />
                  </Box>
                </Box>
              </VStack>
            </Box>

            {/* Gold - First Place */}
            <Box
              bg={cardBg}
              borderRadius="xl"
              textAlign="center"
              position="relative"
              transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
              _hover={{ 
                transform: 'translateY(-8px) scale(1.02)', 
                shadow: '0 20px 40px -12px rgba(251, 191, 36, 0.4)',
              }}
              overflow="hidden"
              flex="1.1"
              maxW="220px"
              boxShadow="0 15px 30px -10px rgba(251, 191, 36, 0.3)"
              zIndex={2}
              mb={4}
              borderWidth="1px"
              borderColor="yellow.200"
            >
              <Box
                h="4px"
                bgGradient="linear(to-r, yellow.300, yellow.400, orange.400, yellow.400, yellow.300)"
                backgroundSize="200% 100%"
                animation="shimmer 2s linear infinite"
                sx={{
                  '@keyframes shimmer': {
                    '0%': { backgroundPosition: '200% 0' },
                    '100%': { backgroundPosition: '-200% 0' },
                  },
                }}
              />
              <VStack p={5} spacing={3}>
                <Flex
                  position="absolute"
                  top={3}
                  left={3}
                  minW="32px"
                  h="32px"
                  px={2}
                  borderRadius="full"
                  bgGradient="linear(to-br, yellow.300, orange.400)"
                  justify="center"
                  align="center"
                  shadow="lg"
                >
                  <Text color="white" fontWeight="bold" fontSize="lg" lineHeight="1">1</Text>
                </Flex>
                
                <Icon as={FaTrophy} color="yellow.400" boxSize={7} filter="drop-shadow(0 2px 4px rgba(251, 191, 36, 0.5))" />
                
                <Box p="3px" borderRadius="full" bgGradient="linear(to-br, yellow.300, orange.400, yellow.500)" shadow="lg">
                  <Avatar 
                    name={top3Businesses[0]?.business_name} 
                    src={businessImages[top3Businesses[0]?.business_name] || ''} 
                    bg="yellow.50" 
                    boxSize="65px"
                    borderWidth="3px"
                    borderColor={cardBg}
                  />
                </Box>
                
                <VStack spacing={0}>
                  <Tooltip label={top3Businesses[0]?.business_name} placement="top" hasArrow openDelay={300}>
                    <Text fontSize="md" fontWeight="bold" noOfLines={1} color={textColor}>
                      {top3Businesses[0]?.business_name}
                    </Text>
                  </Tooltip>
                  <Text 
                    fontWeight="extrabold" 
                    fontSize="2xl" 
                    bgGradient="linear(to-r, purple.500, blue.500)"
                    bgClip="text"
                  >
                    {formatRanking(top3Businesses[0]?.localytics_ranking)}
                  </Text>
                  <Text fontSize="2xs" color={subtitleColor} fontWeight="medium" textTransform="uppercase" letterSpacing="wider">
                    {brandName} Ranking
                  </Text>
                </VStack>
                
                <Box w="full" px={2}>
                  <Box h="5px" bg="yellow.50" borderRadius="full" overflow="hidden">
                    <Box h="full" w="100%" bgGradient="linear(to-r, yellow.400, orange.400)" borderRadius="full" />
                  </Box>
                </Box>
              </VStack>
            </Box>

            {/* Bronze - Third Place */}
            <Box
              bg={cardBg}
              borderRadius="xl"
              textAlign="center"
              position="relative"
              transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
              _hover={{ 
                transform: 'translateY(-4px)', 
                shadow: 'xl',
              }}
              overflow="hidden"
              flex="1"
              maxW="200px"
              boxShadow="lg"
              borderWidth="1px"
              borderColor={borderColor}
            >
              <Box h="3px" bgGradient="linear(to-r, orange.200, orange.400, orange.200)" />
              <VStack p={4} spacing={3}>
                <Flex
                  position="absolute"
                  top={3}
                  left={3}
                  minW="28px"
                  h="28px"
                  px={2}
                  borderRadius="full"
                  bgGradient="linear(to-br, orange.300, orange.500)"
                  justify="center"
                  align="center"
                  shadow="md"
                >
                  <Text color="white" fontWeight="bold" fontSize="md" lineHeight="1">3</Text>
                </Flex>
                
                <Icon as={FaAward} color="orange.400" boxSize={5} />
                
                <Box p="2px" borderRadius="full" bgGradient="linear(to-br, orange.300, orange.500)">
                  <Avatar 
                    name={top3Businesses[2]?.business_name} 
                    src={businessImages[top3Businesses[2]?.business_name] || ''} 
                    bg="orange.50" 
                    boxSize="50px"
                    borderWidth="2px"
                    borderColor={cardBg}
                  />
                </Box>
                
                <VStack spacing={0}>
                  <Tooltip label={top3Businesses[2]?.business_name} placement="top" hasArrow openDelay={300}>
                    <Text fontSize="sm" fontWeight="bold" noOfLines={1} color={textColor}>
                      {top3Businesses[2]?.business_name}
                    </Text>
                  </Tooltip>
                  <Text 
                    fontWeight="extrabold" 
                    fontSize="xl" 
                    bgGradient="linear(to-r, orange.500, orange.600)"
                    bgClip="text"
                  >
                    {formatRanking(top3Businesses[2]?.localytics_ranking)}
                  </Text>
                  <Text fontSize="2xs" color={subtitleColor} fontWeight="medium" textTransform="uppercase" letterSpacing="wider">
                    {brandName} Ranking
                  </Text>
                </VStack>
                
                <Box w="full" px={2}>
                  <Box h="4px" bg="orange.50" borderRadius="full" overflow="hidden">
                    <Box
                      h="full"
                      w={`${Math.max(20, top3Businesses[2]?.localytics_score || 0)}%`}
                      bgGradient="linear(to-r, orange.300, orange.500)"
                      borderRadius="full"
                      transition="width 0.5s ease-out"
                    />
                  </Box>
                </Box>
              </VStack>
            </Box>
          </Flex>
        </Box>
      )}

      {/* Business List */}
      <Box>
        <Flex justify="space-between" align="center" mb={3}>
          <Text fontSize="sm" fontWeight="semibold" color={textColor}>
            All Rankings ({sortedBusinesses.length} businesses)
          </Text>
          <Button
            size="xs"
            variant="ghost"
            colorScheme="gray"
            onClick={() => setShowAllBusinesses(!showAllBusinesses)}
            rightIcon={showAllBusinesses ? <FaChevronUp /> : <FaChevronDown />}
          >
            {showAllBusinesses ? 'Show Less' : 'Show All'}
          </Button>
        </Flex>

        <VStack spacing={0} align="stretch">
          {(showAllBusinesses ? sortedBusinesses : sortedBusinesses.slice(0, 5)).map((business, index) => (
            <BusinessRankingListItem
              key={business.business_name}
              business={business}
              position={index + 1}
              totalBusinesses={totalBusinesses}
              borderColor={borderColor}
              textColor={textColor}
              subtitleColor={subtitleColor}
              cardBg={cardBg}
              hoverBg={hoverBg}
            />
          ))}
        </VStack>

        {!showAllBusinesses && sortedBusinesses.length > 5 && (
          <Text fontSize="xs" color={subtitleColor} textAlign="center" mt={2}>
            +{sortedBusinesses.length - 5} more businesses
          </Text>
        )}
      </Box>
    </MotionBox>
  );
};

export default LocalyticsScoreWidget;
